import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutos
const IDLE_CHECK_INTERVAL_MS = 30 * 1000; // checa a cada 30s
const ACTIVITY_EVENTS = ['click', 'keydown', 'mousedown', 'mousemove', 'scroll', 'touchstart'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));
  
  const updateLastActivity = useCallback(() => {
    if (localStorage.getItem('accessToken')) {
      localStorage.setItem('lastActivityAt', String(Date.now()));
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('lastActivityAt');
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setLoading(false);
  }, []);

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const refresh = localStorage.getItem('refreshToken');
            if (refresh) {
              const response = await axios.post(`${API_URL}/auth/refresh`, {
                refresh_token: refresh,
              });
              const { access_token, refresh_token } = response.data;
              localStorage.setItem('accessToken', access_token);
              localStorage.setItem('refreshToken', refresh_token);
              setAccessToken(access_token);
              setRefreshToken(refresh_token);
              originalRequest.headers.Authorization = `Bearer ${access_token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [logout]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!accessToken) {
        setLoading(false);
        return;
      }
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
        updateLastActivity();        
      } catch (error) {
        logout();
      } finally {
        setLoading(false);
      }
    };
    
    fetchUser();
  }, [accessToken, api, logout, updateLastActivity]);

  useEffect(() => {
    if (!accessToken) return;

    updateLastActivity();

    const handleActivity = () => {
      updateLastActivity();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    const interval = setInterval(() => {
      const lastActivityAt = Number(localStorage.getItem('lastActivityAt') || 0);

      if (!lastActivityAt) return;

      const idleTime = Date.now() - lastActivityAt;

      if (idleTime >= IDLE_TIMEOUT_MS) {
        logout();
        window.location.href = '/admin/login?reason=timeout';
      }
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      clearInterval(interval);
    };
  }, [accessToken, logout, updateLastActivity]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    localStorage.setItem('lastActivityAt', String(Date.now()));
    setAccessToken(access_token);
    setRefreshToken(refresh_token);
    setUser(userData);
    return userData;
  };

  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent' || user?.role === 'admin';



  return (
    <AuthContext.Provider value={{ user, loading, login, logout, api, isAdmin, isAgent }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
